import { ProgramEnum } from '../../models/programs';
import {
    CHANGE_OPTIONS_QUESTION_ANSWER,
    CHANGE_SINGLE_OPTION_QUESTION_ANSWER,
    CHANGE_SINGLE_VALUE_QUESTION_ANSWER,
    CHANGE_STEPS_QUESTION_ANSWER,
    FETCH_PROGRAM_SURVEY_SUCCEEDED,
    FETCH_SEVERAL_PROGRAM_SURVEYS_SUCCEEDED,
    SurveysAction,
} from '../actions/surveysActions';
import {
    GenericSurveyAnswer,
    OptionsAnswerData,
    ProgramSurvey,
    ProgramSurveys,
    SingleOptionAnswerData,
    StepsAnswerData,
    SurveysState,
} from '../models/surveys';
import { surveysDefaultState } from '../models/surveysData';
import { GenericProgramSurvey } from '../models/surveysGeneric';

const defaultState: SurveysState = surveysDefaultState;

export const surveys = (state: SurveysState = defaultState, action: SurveysAction) => {
    const getProgramSurveys = (programId: ProgramEnum): ProgramSurveys =>
        state.programsSurveys[programId];

    const getProgramSingleSurvey = (
        programId: ProgramEnum,
        surveyId: GenericProgramSurvey,
    ): ProgramSurvey | undefined => {
        const programSurveys = getProgramSurveys(programId);

        return programSurveys && programSurveys[surveyId];
    };

    const updateSingleValueAnswerByQuestionId = (
        answers: GenericSurveyAnswer[],
        questionId: number,
        value: string | number | boolean,
    ) =>
        answers.map(answer =>
            answer.question === questionId
                ? {
                      ...answer,
                      data: {
                          content:
                              !answer.data.content || typeof answer.data.content === typeof value
                                  ? value
                                  : answer.data.content,
                      },
                  }
                : answer,
        );

    const updateOptionsAnswerByQuestionId = (
        answers: GenericSurveyAnswer[],
        questionId: number,
        field: string,
        value: string,
    ) =>
        answers.map(answer => {
            if (answer.question != questionId) {
                return answer;
            }

            if (field === 'content') {
                return {
                    ...answer,
                    data: {
                        ...answer.data,
                        content: value,
                    },
                };
            }

            const options: string[] = (answer && (answer.data as OptionsAnswerData).options) || [];
            const updatedOptions = options.includes(value)
                ? options.filter(option => option != value)
                : [...options, value];

            return {
                ...answer,
                data: {
                    ...answer.data,
                    options: updatedOptions,
                },
            };
        });

    const updateSingleOptionAnswerByQuestionId = (
        answers: GenericSurveyAnswer[],
        questionId: number,
        field: string,
        value: string,
    ) =>
        answers.map(answer => {
            if (answer.question != questionId) {
                return answer;
            }

            if (field === 'content') {
                return {
                    ...answer,
                    data: {
                        ...answer.data,
                        content: value,
                    },
                };
            }

            return {
                ...answer,
                data: {
                    ...answer.data,
                    option:
                        (answer.data as SingleOptionAnswerData).option === value
                            ? undefined
                            : value,
                },
            };
        });

    const updateStepAnswerByQuestionId = (
        answers: GenericSurveyAnswer[],
        questionId: number,
        stepId: string,
        value: string,
    ) =>
        answers.map(answer => {
            if (answer.question !== questionId) {
                return answer;
            }

            const stepAnswers = (answer.data as StepsAnswerData).content;

            if (!stepAnswers) {
                return {
                    ...answer,
                    data: {
                        content: [{ id: stepId, content: value }],
                    },
                };
            }

            const stepAnswerToUpdate = stepAnswers.find(stepAnswer => stepAnswer.id === stepId);

            return {
                ...answer,
                data: {
                    content: stepAnswerToUpdate
                        ? stepAnswers.map(stepAnswer =>
                              stepAnswer.id === stepId
                                  ? {
                                        ...stepAnswer,
                                        content: value,
                                    }
                                  : stepAnswer,
                          )
                        : [...stepAnswers, { id: stepId, content: value }],
                },
            };
        });

    switch (action.type) {
        case FETCH_PROGRAM_SURVEY_SUCCEEDED: {
            const { programId, surveyId, questions, answers } = action.payload;

            if (!Object.values(ProgramEnum).includes(programId)) {
                return state;
            }

            const programSurveys = getProgramSurveys(programId);

            return {
                ...state,
                programsSurveys: {
                    ...state.programsSurveys,
                    [programId]: {
                        ...programSurveys,
                        [surveyId]: {
                            questions,
                            answers,
                        },
                    },
                },
            };
        }

        case FETCH_SEVERAL_PROGRAM_SURVEYS_SUCCEEDED: {
            const { programId, programSurveys } = action.payload;

            const prevLoadedProgramSurveys = getProgramSurveys(programId);

            return {
                ...state,
                programsSurveys: {
                    ...state.programsSurveys,
                    [programId]: {
                        ...prevLoadedProgramSurveys,
                        ...programSurveys,
                    },
                },
            };
        }

        case CHANGE_SINGLE_VALUE_QUESTION_ANSWER: {
            const { programId, surveyId, question, value } = action.payload;

            const surveyToUpdate = getProgramSingleSurvey(programId, surveyId);

            if (!surveyToUpdate) {
                return state;
            }

            return {
                ...state,
                programsSurveys: {
                    ...state.programsSurveys,
                    [programId]: {
                        ...getProgramSurveys(programId),
                        [surveyId]: {
                            questions: surveyToUpdate.questions,
                            answers: updateSingleValueAnswerByQuestionId(
                                surveyToUpdate.answers,
                                question,
                                value,
                            ),
                        },
                    },
                },
            };
        }

        case CHANGE_OPTIONS_QUESTION_ANSWER: {
            const { programId, surveyId, question, field, value } = action.payload;

            const surveyToUpdate = getProgramSingleSurvey(programId, surveyId);

            if (!surveyToUpdate) {
                return state;
            }

            return {
                ...state,
                programsSurveys: {
                    ...state.programsSurveys,
                    [programId]: {
                        ...getProgramSurveys(programId),
                        [surveyId]: {
                            questions: surveyToUpdate.questions,
                            answers: updateOptionsAnswerByQuestionId(
                                surveyToUpdate.answers,
                                question,
                                field,
                                value,
                            ),
                        },
                    },
                },
            };
        }

        case CHANGE_SINGLE_OPTION_QUESTION_ANSWER: {
            const { programId, surveyId, question, field, value } = action.payload;

            const surveyToUpdate = getProgramSingleSurvey(programId, surveyId);

            if (!surveyToUpdate) {
                return state;
            }

            return {
                ...state,
                programsSurveys: {
                    ...state.programsSurveys,
                    [programId]: {
                        ...getProgramSurveys(programId),
                        [surveyId]: {
                            questions: surveyToUpdate.questions,
                            answers: updateSingleOptionAnswerByQuestionId(
                                surveyToUpdate.answers,
                                question,
                                field,
                                value,
                            ),
                        },
                    },
                },
            };
        }

        case CHANGE_STEPS_QUESTION_ANSWER: {
            const { programId, surveyId, question, stepId, value } = action.payload;

            const surveyToUpdate = getProgramSingleSurvey(programId, surveyId);

            if (!surveyToUpdate) {
                return state;
            }

            return {
                ...state,
                programsSurveys: {
                    ...state.programsSurveys,
                    [programId]: {
                        ...getProgramSurveys(programId),
                        [surveyId]: {
                            questions: surveyToUpdate.questions,
                            answers: updateStepAnswerByQuestionId(
                                surveyToUpdate.answers,
                                question,
                                stepId,
                                value,
                            ),
                        },
                    },
                },
            };
        }

        default:
            return state;
    }
};
